/**
 * A list that rests with its last line in the middle, as song lyrics do, unless the user looks back.
 *
 *  - At rest the last line (the last `data-line`) is centred, and `--fade-end` on the list says where it
 *    starts: the lines above fade out from the top edge down to it, the last line is never faded. What
 *    follows it without a `data-line` (the user's words) is not a line to scroll to.
 *  - Looking back starts with a gesture back (the wheel up, a drag down, a scroll that moves the last line
 *    off the middle), even when the list is short.
 *  - It ends only when asked: `toEnd` (the button), or any of `keys` changing (a new answer, a new turn).
 *  - While looking back the line nearest the middle is in focus (`data-line` names it; null until the
 *    first scroll, when the last line is the one in the middle). The list keeps room above and below its
 *    lines so any line can reach the middle.
 *  - At rest the last line growing (fitting) or the list changing size keeps the last line centred.
 *
 * `surface` goes on the area around the list: a drag or the wheel there scrolls the list too. The canvas
 * is scaled, so a finger's pixels are converted. State changes only in handlers and while rendering (a
 * change of `keys`); the effects only move the list.
 */
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type WheelEvent } from 'react';

/** The last line further from the middle than this (px), a scroll is a look back. */
const AT_REST_PX = 12;
/** A drag shorter than this (px) is a tap, not a look back. */
const DRAG_PX = 6;

const sameKeys = (a: unknown[], b: unknown[]) => a.length === b.length && a.every((k, i) => Object.is(k, b[i]));

/** The canvas is scaled: screen pixels over the list's own. */
const scaleOf = (list: HTMLElement) => list.getBoundingClientRect().height / (list.offsetHeight || 1) || 1;

function lastLine(list: HTMLElement): HTMLElement | null {
  const lines = list.querySelectorAll<HTMLElement>('[data-line]');
  return lines[lines.length - 1] ?? null;
}

/** How far (list px) the middle of `line` is below the middle of the list. */
function offMiddle(list: HTMLElement, line: HTMLElement): number {
  const box = list.getBoundingClientRect();
  const r = line.getBoundingClientRect();
  return (r.top + r.height / 2 - (box.top + box.height / 2)) / scaleOf(list);
}

/** Bring the last line to the middle. */
function centre(list: HTMLElement | null) {
  const line = list && lastLine(list);
  if (list && line) list.scrollTop += offMiddle(list, line);
}

/** At rest: the last line in the middle, and the list faded down to where it starts. */
function rest(list: HTMLElement | null) {
  const line = list && lastLine(list);
  if (!list || !line) return;
  centre(list);
  const top = (line.getBoundingClientRect().top - list.getBoundingClientRect().top) / scaleOf(list);
  list.style.setProperty('--fade-end', `${Math.max(0, Math.round(top))}px`);
}

export function useScrollBack(keys: unknown[]) {
  const ref = useRef<HTMLDivElement>(null);
  const [browsing, setBrowsing] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const [seenKeys, setSeenKeys] = useState(keys);
  // the handlers and the observer read this; React state is a render behind them
  const browsingNow = useRef(false);
  const drag = useRef<{ y: number; outside: boolean } | null>(null);

  // a new answer or a new turn: back to rest (adjusting state while rendering, not in an effect)
  if (!sameKeys(keys, seenKeys)) {
    setSeenKeys(keys);
    setBrowsing(false);
    setFocused(null);
  }

  // back at rest, or new lines at rest: the last line in the middle. Looking back shows the lines kept out
  // of the way at rest: the list keeps the last line where it was while they appear above it.
  useLayoutEffect(() => {
    browsingNow.current = browsing;
    if (browsing) centre(ref.current);
    else rest(ref.current);
  }, [browsing, seenKeys]);

  useEffect(() => {
    const list = ref.current;
    const line = list && lastLine(list);
    if (!list || !line) return;
    const observer = new ResizeObserver(() => {
      if (!browsingNow.current) rest(list);
    });
    observer.observe(line);
    observer.observe(list);
    return () => observer.disconnect();
  });

  const lookBack = () => {
    if (browsingNow.current) return;
    browsingNow.current = true;
    setBrowsing(true);
  };

  const toEnd = () => {
    browsingNow.current = false;
    setBrowsing(false);
    setFocused(null);
  };

  const onScroll = () => {
    const list = ref.current;
    const line = list && lastLine(list);
    if (!list || !line) return;
    if (!browsingNow.current && Math.abs(offMiddle(list, line)) > AT_REST_PX) lookBack();
    if (browsingNow.current) setFocused(lineNearestMiddle(list));
  };

  const surface = {
    onWheel: (e: WheelEvent) => {
      if (e.deltaY < 0) lookBack();
      if (!ref.current?.contains(e.target as Node)) ref.current?.scrollBy({ top: e.deltaY });
    },
    onPointerDown: (e: PointerEvent) => {
      if ((e.target as Element).closest?.('button')) return;
      drag.current = { y: e.clientY, outside: !ref.current?.contains(e.target as Node) };
    },
    onPointerMove: (e: PointerEvent) => {
      const list = ref.current;
      const d = drag.current;
      if (!d || !list) return;
      const dy = e.clientY - d.y;
      if (dy > DRAG_PX) lookBack(); // a finger moving down brings earlier lines into view
      if (d.outside) list.scrollTop -= dy / scaleOf(list);
      if (d.outside || Math.abs(dy) > DRAG_PX) d.y = e.clientY;
    },
    onPointerUp: () => { drag.current = null; },
    onPointerCancel: () => { drag.current = null; },
    onPointerLeave: () => { drag.current = null; },
  };

  return { ref, browsing, focused, onScroll, surface, toEnd };
}

/** The `data-line` of the line whose middle is nearest the list's middle. */
function lineNearestMiddle(list: HTMLElement): string | null {
  let best: string | null = null;
  let distance = Infinity;
  for (const line of list.querySelectorAll<HTMLElement>('[data-line]')) {
    const d = Math.abs(offMiddle(list, line));
    if (d < distance) {
      distance = d;
      best = line.dataset.line ?? null;
    }
  }
  return best;
}
